import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, API } from "../../lib/api";

export function ImageDropzone({ value, onChange, testid = "image-dropzone" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Convert stored path to full URL for preview (paths like /okome-1.jpg or /api/files/xxx)
  const previewSrc = value
    ? value.startsWith("http") || value.startsWith("blob:")
      ? value
      : value.startsWith("/api/")
      ? `${API.replace(/\/api$/, "")}${value}`
      : value
    : "";

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragging ? "border-gold bg-gold/10" : "border-ink/20 hover:border-ink/40 bg-surface"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-ink/60">
            <Loader2 className="animate-spin" size={22} />
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-ink/60">
            <UploadCloud size={22} />
            <span className="text-sm">
              Drop image here, or <span className="text-gold underline">click to browse</span>
            </span>
            <span className="text-xs text-ink/40">JPG, PNG, WebP, GIF · up to 10 MB</span>
          </div>
        )}
      </div>

      {previewSrc && (
        <div className="relative inline-block">
          <img src={previewSrc} alt="preview" className="h-28 w-28 object-cover rounded" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="absolute -top-2 -right-2 bg-ink text-cream rounded-full p-1 hover:bg-destructive"
            aria-label="Remove image"
          >
            <X size={12} />
          </button>
        </div>
      )}

      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="or paste an image URL"
        data-testid={`${testid}-url`}
        className="w-full text-xs px-3 py-2 border border-ink/15 rounded bg-cream text-ink/70"
      />
    </div>
  );
}
